import React from 'react';
import { Copy, Lightbulb, Loader2, MessageSquare, Volume2 } from 'lucide-react';
import { CONCEPT_ICONS } from '../../utils/concept-icons';
import { useLocale } from '../../contexts/LocaleContext';
import { isDocumentSpeechSupported } from '../../utils/document-speech';
import {
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import {
  ScriptInfoStats,
  type ScriptInfoStatsLabels,
} from '../script-info/ScriptInfoStats';

const NoteIcon = CONCEPT_ICONS.note;

export type BlockMenuStatLabels = {
  words: string;
  minutes: string;
  pages: string;
};

export function BlockMenuAnalyzeItem({
  isAnalyzing,
  disableReason,
  onAnalyze,
}: {
  isAnalyzing: boolean;
  disableReason: string | undefined;
  onAnalyze: () => void;
}): React.ReactElement {
  const { t } = useLocale();
  const label = isAnalyzing ? t('analyzing') : t('analyze');
  const icon = isAnalyzing ? (
    <Loader2 className="h-4 w-4 animate-spin" />
  ) : (
    <Lightbulb className="h-4 w-4" />
  );
  const tooltip = isAnalyzing
    ? t('ongoing_analysis_please_wait')
    : disableReason;

  if (tooltip) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div>
              <DropdownMenuItem
                disabled
                className="gap-2"
                onSelect={(event) => event.preventDefault()}
              >
                {icon}
                {label}
              </DropdownMenuItem>
            </div>
          </TooltipTrigger>
          <TooltipContent side="right" className="z-[2200] max-w-[16rem]">
            {tooltip}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <DropdownMenuItem
      className="gap-2"
      onSelect={() => {
        onAnalyze();
      }}
    >
      {icon}
      {label}
    </DropdownMenuItem>
  );
}

export function BlockMenuChatItem({
  onChat,
  labelKey = 'chat_about_this_section',
}: {
  onChat: () => void;
  labelKey?: 'chat_about_this_section' | 'chat_about_this_paragraph';
}): React.ReactElement {
  const { t } = useLocale();

  return (
    <DropdownMenuItem
      className="gap-2"
      onSelect={() => {
        onChat();
      }}
    >
      <MessageSquare className="h-4 w-4" />
      {t(labelKey)}
    </DropdownMenuItem>
  );
}

export function BlockMenuCopyItem({ onCopy }: { onCopy: () => void }): React.ReactElement {
  const { t } = useLocale();

  return (
    <DropdownMenuItem
      className="gap-2"
      onSelect={() => {
        onCopy();
      }}
    >
      <Copy className="h-4 w-4" />
      {t('copy')}
    </DropdownMenuItem>
  );
}

export function BlockMenuReadAloudItem({
  onReadAloud,
}: {
  onReadAloud: () => void;
}): React.ReactElement | null {
  const { t } = useLocale();

  if (!isDocumentSpeechSupported()) {
    return null;
  }

  return (
    <DropdownMenuItem
      className="gap-2"
      onSelect={() => {
        onReadAloud();
      }}
    >
      <Volume2 className="h-4 w-4" />
      {t('read_aloud')}
    </DropdownMenuItem>
  );
}

export function BlockMenuMoveToNoteItem({
  onMoveToNote,
  disabled = false,
}: {
  onMoveToNote: () => void;
  disabled?: boolean;
}): React.ReactElement {
  const { t } = useLocale();

  return (
    <DropdownMenuItem
      className="gap-2"
      disabled={disabled}
      onSelect={() => {
        onMoveToNote();
      }}
    >
      <NoteIcon className="h-4 w-4" />
      {t('move_to_note')}
    </DropdownMenuItem>
  );
}

export function BlockMenuStatsSection({
  labels,
}: {
  labels: BlockMenuStatLabels;
}): React.ReactElement {
  return (
    <>
      <DropdownMenuSeparator />
      <div className="px-2 py-1.5 text-xs leading-5 text-muted-foreground tabular-nums">
        <div>{labels.words}</div>
        <div>{labels.minutes}</div>
        <div>{labels.pages}</div>
      </div>
    </>
  );
}

export type BlockMenuScriptInfoLabels = ScriptInfoStatsLabels;

export function BlockMenuScriptInfoSection({
  labels,
}: {
  labels: BlockMenuScriptInfoLabels;
}): React.ReactElement {
  return (
    <>
      <DropdownMenuSeparator />
      <div className="px-2 py-1.5">
        <ScriptInfoStats labels={labels} />
      </div>
    </>
  );
}
